import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetTitle } from '../../ui/sheet';
import { X, Check, MessageCircle } from '../../../icons';
import {
  type Icon,
  PlusCircle,
  SquaresFour,
  UsersThree,
  VideoCamera,
  Image as ImageIcon,
} from '../../../design-system/icons';
import { Button } from '../../ui/button';

import { cn } from '../../../lib/utils';
import type { ReviewThreadEntry } from '../../../lib/models';
import { ChatThread } from '../ChatThread';
import { useCampaignShare } from '../../../campaign-detail/share/useCampaignShare';
import { SharePopover } from '../../../campaign-detail/share/SharePopover';
import type { ShortShareInput } from '../../../campaign-detail/share/shortLink';
import { DISPLAY_DECIMALS, formatCompact, formatMoneyExact } from '../../../lib/format';
import { PLATFORM_COLOR, resolvePlatform } from '@discovr/types';

export type CreatorReviewDeliverable = {
  type?: string;
  base_price?: number;
  is_selected?: boolean;
};

export type CreatorReviewPanelProps = {
  open: boolean;
  onClose: () => void;

  creatorName: string;
  creatorAvatarUrl?: string;
  creatorEmail?: string;
  creatorHandle?: string;
  /** Platform username for the admin-only share deep-link (opens this
   *  conversation in the brand app). Share icon hidden when absent / not admin. */
  shareCreatorUsername?: string;

  followers?: number;
  avgViews?: number;
  engagementRate?: number;

  statusLabel?: string;
  statusTone?: 'default' | 'pending' | 'approved' | 'rejected' | 'revision';

  deliverables?: CreatorReviewDeliverable[];
  totalCost?: number;

  thread?: ReviewThreadEntry[];
  threadLoading?: boolean;
  threadError?: boolean;
  onThreadRetry?: () => void;
  canAddComment?: boolean;
  /** Thread is locked (approved -> read-only). Forwarded to ChatThread so
   *  Edit/Delete gate on the LOCK rather than on `canAddComment`, which at some
   *  call sites also encodes "is a post handler wired?" and would otherwise
   *  strip both affordances from an open thread. Omitted: ChatThread falls back
   *  to `canAddComment === false`. */
  locked?: boolean;
  addCommentBusy?: boolean;
  onAddComment?: (body: string, replyTo?: string) => void | Promise<void>;
  onEditMessage?: (id: string, body: string) => void | Promise<void>;
  onDeleteMessage?: (id: string) => void | Promise<void>;
  /** True only for the Super Admin tier — lets ChatThread's delete
   *  affordance bypass the locked-thread gate (server permission flags
   *  already scope who may actually delete what). */
  viewerIsSuperAdmin?: boolean;
  /** Read-only viewer: composer stays visible but disabled (no posting). */
  readOnly?: boolean;
  draft?: string;
  setDraft?: (v: string) => void;
  viewerActor?: string;

  // ── Review actions (Figma: Approve / Reject in the header) ────────────────
  /** Show the Approve / Reject buttons in the conversation header. */
  canReview?: boolean;
  /** Disables Approve when there is nothing to approve (e.g. no deliverable picked). */
  approveDisabled?: boolean;
  reviewBusy?: boolean;
  onApprove?: () => void | Promise<void>;
  onReject?: () => void | Promise<void>;

  title?: string;

  /** D5: when true, the conversation toggle button itself is not rendered
   *  at all (not just disabled) — used when a public grant's
   *  allow_chat_readonly was off at mint time, so there is genuinely
   *  nothing to show, not an empty state to reveal. Defaults false — every
   *  existing authed caller is unaffected. Mirrors ScriptReviewPanel /
   *  ContentReviewPanel's own `hideThreadToggle`. */
  hideThreadToggle?: boolean;

  /** Forces the conversation column open on mount regardless of `thread`'s
   *  length at that instant. Needed by authed callers (ReviewThreadDrawer)
   *  whose thread query hasn't resolved yet on first render — without this,
   *  `threadOpen`'s one-time init would see an empty `thread` and lock in
   *  closed, even though real messages load moments later. Mirrors
   *  ScriptReviewPanel/ContentReviewPanel's own `threadDefaultOpen`. */
  threadDefaultOpen?: boolean;
};

function statusPillClass(tone: CreatorReviewPanelProps['statusTone']): string {
  switch (tone) {
    case 'approved':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 ring-rose-200';
    case 'pending':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'revision':
      return 'bg-violet-50 text-violet-700 ring-violet-200';
    default:
      return 'bg-muted text-foreground ring-ring';
  }
}

// Instagram brand gradient (approved for deliverable-card icon tiles — a brand
// mark, not the AI gradient).
const IG_GRADIENT =
  'linear-gradient(135deg,#FEDA75 0%,#FA7E1E 25%,#D62976 50%,#962FBF 75%,#4F5BD5 100%)';

// Phosphor's own icon component type — matches the design-system icon exports
// (weight is IconWeight, not a loose string).
type Glyph = Icon;

/** Pick a representative glyph for a deliverable type. */
function deliverableGlyph(type?: string): Glyph {
  const t = (type || '').toLowerCase();
  if (t.includes('stor')) return PlusCircle;
  if (t.includes('carousel')) return SquaresFour;
  if (t.includes('collab')) return UsersThree;
  if (t.includes('post') || t.includes('static') || t.includes('image')) return ImageIcon;
  return VideoCamera;
}

/** IG keeps its gradient; the rest come from the shared platform map. */
function platformSwatch(platform: string | null | undefined): string {
  const p = resolvePlatform(platform);
  if (p === 'instagram') return IG_GRADIENT;
  return p ? PLATFORM_COLOR[p] : IG_GRADIENT;
}

export function CreatorReviewPanel({
  open,
  onClose,
  creatorName,
  creatorAvatarUrl,
  creatorEmail,
  creatorHandle,
  shareCreatorUsername,
  followers,
  avgViews,
  engagementRate,
  statusLabel,
  statusTone = 'default',
  deliverables,
  totalCost,
  thread,
  threadLoading,
  threadError,
  onThreadRetry,
  canAddComment,
  locked,
  addCommentBusy,
  onAddComment,
  onEditMessage,
  onDeleteMessage,
  viewerIsSuperAdmin,
  readOnly,
  draft,
  setDraft,
  viewerActor = 'brand',
  canReview,
  approveDisabled,
  reviewBusy,
  onApprove,
  onReject,
  title = 'Creator conversation',
  hideThreadToggle = false,
  threadDefaultOpen = false,
}: CreatorReviewPanelProps) {
  // Creator drawer differs from Script/Content review: the conversation is OPEN
  // by default WHEN there is something to show, OR when the caller forces it
  // via threadDefaultOpen (authed callers whose thread query hasn't resolved
  // yet on mount — see the prop's doc comment). Mirrors ScriptReviewPanel /
  // ContentReviewPanel's own init exactly (`threadDefaultOpen ||
  // thread.length > 0`) — so a thread with no messages (D6 toggle-off grant,
  // no override passed) never starts open on an empty column.
  const [threadOpen, setThreadOpen] = useState(
    () => threadDefaultOpen || (!!thread && thread.length > 0),
  );
  // Admin-only share deep-link for this conversation (brand-app link).
  const { canShare, makeShortUrl } = useCampaignShare();
  const shareInput: ShortShareInput | null = shareCreatorUsername
    ? { kind: 'creator', username: shareCreatorUsername, purpose: 'chat' }
    : null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const engRateLabel =
    engagementRate != null && Number.isFinite(engagementRate) && engagementRate > 0
      ? `${Number(engagementRate).toFixed(DISPLAY_DECIMALS)}%`
      : '—';

  const showReview = !!canReview && (!!onApprove || !!onReject);

  // Plain inline style, not a Tailwind arbitrary-value class — Tailwind's
  // JIT never generates a rule for a nested min()/calc() class (verified: no
  // matching rule in any loaded stylesheet), silently falling back to
  // content width. max-[768px]:w-full! still wins on narrow viewports via
  // !important, which beats an inline style.
  const panelWidth = threadOpen
    ? { width: 'min(960px, calc(100vw - 6rem))' }
    : { width: 'min(560px, calc(100vw - 15rem))' };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        showCloseButton={false}
        aria-label={title}
        style={panelWidth}
        className="flex flex-col gap-0 bg-card p-0 transition-[width] duration-200 sm:max-w-none max-[768px]:w-full!"
        // Inert everywhere except the public share shell, which reads this
        // attribute in its click-capture allow-list (see
        // PublicCampaignShell.tsx's file header) — lets every read-only
        // interaction inside this panel (Approve/Reject/Close, scroll) work
        // normally on a public 'chat'-purpose link, same established pattern
        // as ScriptReviewPanel/ContentReviewPanel/PublicInsightsShell's own
        // `data-public-allowed-subtree`. Does nothing in the authed app (no
        // capture listener reads it there).
        data-public-allowed-subtree=""
      >
        <SheetTitle className="sr-only">{title ?? 'Creator review'}</SheetTitle>

        {/* Full-width top bar — creator info (left) + actions (right). It
            stretches as the panel widens; the conversation spawns in from the
            right below it. No section labels (the drawer is self-evident). */}
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-card px-5">
          <div
            className="size-9 shrink-0 rounded-full border border-border bg-muted bg-cover bg-center"
            style={creatorAvatarUrl ? { backgroundImage: `url(${creatorAvatarUrl})` } : undefined}
            aria-hidden
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="truncate text-body-sm-regular/5 font-medium text-foreground">{creatorName}</span>
              {statusLabel ? (
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ring-1 ring-inset',
                    statusPillClass(statusTone),
                  )}
                >
                  {statusLabel}
                </span>
              ) : null}
            </div>
            {creatorHandle ? (
              <span className="truncate text-caption-regular/4 font-medium text-muted-foreground">{creatorHandle}</span>
            ) : null}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Approve / Reject — match the review DrawerHeader Approve pill
                (solid, rounded-full); Reject is the same pill in red. */}
            {showReview && onApprove ? (
              <Button
                type="button"
                onClick={() => void onApprove()}
                disabled={reviewBusy || approveDisabled}
                className="inline-flex h-10 items-center rounded-full bg-[#18181B] px-5 text-sm font-medium text-white hover:bg-[#27272A]"
              >
                Approve
              </Button>
            ) : null}
            {showReview && onReject ? (
              <Button
                type="button"
                onClick={() => void onReject()}
                disabled={reviewBusy}
                className="inline-flex h-10 items-center rounded-full bg-rose-600 px-5 text-sm font-medium text-white hover:bg-rose-700"
              >
                Reject
              </Button>
            ) : null}
            {!hideThreadToggle && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setThreadOpen((v) => !v)}
                className={cn(
                  'relative size-10 shrink-0 rounded-full border p-0',
                  threadOpen
                    ? 'border-foreground bg-muted text-foreground hover:bg-muted'
                    : 'border-border bg-card text-foreground hover:bg-accent',
                )}
                aria-pressed={threadOpen}
                aria-label="Conversation"
              >
                <MessageCircle size={16} />
              </Button>
            )}
            {/* Admin-only share — copies a brand-app deep link that reopens this
                conversation. Sits immediately left of close, matching every
                other drawer. */}
            {canShare && shareCreatorUsername ? (
              <SharePopover
                input={shareInput}
                getUrl={() => (shareInput ? makeShortUrl(shareInput) : null)}
              />
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent"
              aria-label="Close"
            >
              <X size={18} />
            </Button>
          </div>
        </div>

        {/* Body: review (left) | conversation (right), divided by a vertical
            border. The conversation spawns in from the right, pushing the review
            content to the left. */}
        <div className="flex min-h-0 flex-1">
          {/* Review body: creator snapshot + deliverables */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {creatorEmail ? (
                <p className="mb-4 truncate text-caption-regular text-muted-foreground">{creatorEmail}</p>
              ) : null}

              <h4 className="mb-3 text-body-sm-medium text-foreground">Creator snapshot</h4>
              <div className="grid grid-cols-3 gap-3">
                <StatCell label="Subscribers" value={followers && followers > 0 ? formatCompact(followers) : '—'} />
                <StatCell label="Avg. views" value={avgViews && avgViews > 0 ? formatCompact(avgViews) : '—'} />
                <StatCell label="Eng. rate" value={engRateLabel} />
              </div>

              <div className="mt-6 space-y-3">
                <h4 className="text-body-sm-medium text-foreground">Deliverables</h4>
                <div className="space-y-2.5">
                  {!deliverables || deliverables.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-4 text-center text-caption-regular text-muted-foreground italic">
                      No deliverables set
                    </div>
                  ) : (
                    <>
                      {deliverables.map((d, i) => {
                        const price = Number(d.base_price ?? 0);
                        const platform = resolvePlatform(d.type);
                        const Glyph = deliverableGlyph(d.type);
                        return (
                          <div
                            key={`${d.type}-${i}`}
                            className={cn(
                              'flex items-center gap-3 rounded-xl border bg-card p-3',
                              d.is_selected ? 'border-foreground' : 'border-border',
                            )}
                          >
                            <span
                              className="flex size-9 shrink-0 items-center justify-center rounded-lg text-white"
                              style={{ background: platformSwatch(platform) }}
                            >
                              <Glyph size={18} weight="fill" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-body-sm-semibold text-foreground capitalize">
                                {(d.type || 'deliverable').replace(/_/g, ' ')}
                              </div>
                              <div className="text-body-sm-medium text-muted-foreground">
                                {price > 0 ? formatMoneyExact(price) : '—'}
                              </div>
                            </div>
                            {d.is_selected ? (
                              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                                <Check size={12} weight="bold" />
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                      {totalCost != null && totalCost > 0 ? (
                        <div className="flex items-center justify-between rounded-xl border border-border bg-muted px-4 py-3">
                          <span className="text-caption-medium tracking-wider text-muted-foreground uppercase">
                            Total cost
                          </span>
                          <span className="text-body-md-semibold text-foreground">
                            {formatMoneyExact(totalCost)}
                          </span>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>

          {/* Conversation — spawns from the right, headerless (the top bar is
              self-evident, so no section label) */}
          {threadOpen ? (
            <div className="flex min-h-0 w-[360px] shrink-0 flex-col border-l border-border bg-card max-[768px]:w-full">
              <ChatThread
                entries={thread}
                isLoading={threadLoading}
                isError={threadError}
                onRetry={onThreadRetry}
                canAddComment={canAddComment}
                locked={locked}
                addCommentBusy={addCommentBusy}
                onAddComment={
                  onAddComment
                    ? ({ body, replyTo }) => onAddComment(body, replyTo)
                    : undefined
                }
                onEditMessage={onEditMessage}
                onDeleteMessage={onDeleteMessage}
                viewerIsSuperAdmin={viewerIsSuperAdmin}
                enableReplies
                readOnly={readOnly}
                draft={draft}
                setDraft={setDraft}
                outgoingActor={viewerActor}
                composerPlaceholder="Type a message to the creator or talent manager…"
                emptyStateText="No messages yet. Start the conversation between the brand and creator."
              />
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card px-3 py-2.5">
      <span className="text-caption-regular text-muted-foreground">{label}</span>
      <span className="mt-1 text-heading-h7 font-medium text-foreground">{value}</span>
    </div>
  );
}

export default CreatorReviewPanel;
